import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import api from '../services/api';
import { CategoryWithSubcategories, OrderSubcategory } from '../types/order';

type Row = OrderSubcategory & { category_name: string };

function flatten(cats: CategoryWithSubcategories[]): Row[] {
  const out: Row[] = [];
  for (const c of cats || []) {
    for (const s of c.subcategories || []) {
      out.push({ ...s, category_name: c.name });
    }
  }
  return out.sort((a, b) => {
    const ca = a.category_name.localeCompare(b.category_name);
    if (ca !== 0) return ca;
    return (a.display_order ?? 0) - (b.display_order ?? 0) || a.name.localeCompare(b.name);
  });
}

type DialogState = {
  open: boolean;
  mode: 'add' | 'edit';
  categoryId: string;
  subcategoryId: string;
  label: string;
  file: File | null;
};

const EMPTY_DIALOG: DialogState = {
  open: false,
  mode: 'add',
  categoryId: '',
  subcategoryId: '',
  label: '',
  file: null,
};

interface OrderTermsSettingsTabProps {
  onError?: (msg: string) => void;
}

const OrderTermsSettingsTab: React.FC<OrderTermsSettingsTabProps> = ({ onError }) => {
  const [loading, setLoading] = useState(true);
  const [allRows, setAllRows] = useState<Row[]>([]);
  const [cats, setCats] = useState<CategoryWithSubcategories[]>([]);
  const [dlg, setDlg] = useState<DialogState>(EMPTY_DIALOG);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [categories, subcategories] = await Promise.all([
        api.adminListCategories(),
        api.adminListSubcategories(),
      ]);
      const merged: CategoryWithSubcategories[] = (categories || []).map((cat) => ({
        ...cat,
        subcategories: (subcategories || []).filter((s) => s.category_id === cat.id),
      }));
      setCats(merged);
      setAllRows(flatten(merged));
    } catch (e: unknown) {
      onError?.((e as Error)?.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  const withTerms = allRows.filter((r) => r.terms_pdf_path);
  const withoutTerms = allRows.filter((r) => !r.terms_pdf_path);

  const openAdd = () => setDlg({ ...EMPTY_DIALOG, open: true, mode: 'add' });

  const openEdit = (r: Row) =>
    setDlg({ open: true, mode: 'edit', categoryId: r.category_id, subcategoryId: r.id, label: r.terms_label || '', file: null });

  const closeDlg = () => setDlg(EMPTY_DIALOG);

  const handleSave = async () => {
    if (!dlg.subcategoryId) { onError?.('Please select a subcategory.'); return; }
    if (!dlg.file && dlg.mode === 'add') { onError?.('Please choose a PDF file.'); return; }
    setSaving(true);
    try {
      if (dlg.file) {
        await api.adminUploadSubcategoryTerms(dlg.subcategoryId, dlg.file, dlg.label.trim() || undefined);
      } else if (dlg.mode === 'edit') {
        await api.adminUpdateSubcategory(dlg.subcategoryId, { terms_label: dlg.label.trim() || null });
      }
      setSuccessMsg('Terms document saved successfully.');
      closeDlg();
      await load();
    } catch (e: unknown) {
      const msg = (e as any)?.response?.data?.detail || (e as Error)?.message || 'Save failed';
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r: Row) => {
    if (!window.confirm(`Remove terms PDF for "${r.name}"?`)) return;
    setDeleting(r.id);
    try {
      await api.adminDeleteSubcategoryTerms(r.id);
      setSuccessMsg('Terms document removed.');
      await load();
    } catch (e: unknown) {
      const msg = (e as any)?.response?.data?.detail || (e as Error)?.message || 'Delete failed';
      onError?.(msg);
    } finally {
      setDeleting(null);
    }
  };

  const selectedSubcategory = allRows.find((r) => r.id === dlg.subcategoryId);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Catalogue terms &amp; conditions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assign a terms PDF to each subcategory. Customers will be shown the applicable documents
              on the order review page when an approval link is sent.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ flexShrink: 0 }}
          >
            Add terms document
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {withTerms.length === 0 ? (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              py: 5,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <UploadFileIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography variant="body1" fontWeight={600}>No terms documents uploaded yet</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
              Click "Add terms document" to assign a PDF to a subcategory.
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openAdd}>
              Add terms document
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Subcategory</TableCell>
                  <TableCell>Customer label</TableCell>
                  <TableCell>PDF</TableCell>
                  <TableCell width={120} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withTerms.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.category_name}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.terms_label || <em style={{ opacity: 0.5 }}>No label set</em>}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        <Typography variant="body2" color="success.main">Uploaded</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit label / replace PDF">
                        <IconButton size="small" onClick={() => openEdit(r)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove PDF">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleting === r.id}
                          onClick={() => void handleDelete(r)}
                        >
                          {deleting === r.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add / Edit dialog */}
      <Dialog open={dlg.open} onClose={closeDlg} maxWidth="sm" fullWidth>
        <DialogTitle>{dlg.mode === 'add' ? 'Add terms document' : 'Edit terms document'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>

          {/* Category → Subcategory selectors */}
          {dlg.mode === 'add' ? (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={dlg.categoryId}
                  onChange={(e) =>
                    setDlg((d) => ({ ...d, categoryId: e.target.value as string, subcategoryId: '' }))
                  }
                >
                  {cats.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={!dlg.categoryId}>
                <InputLabel>Subcategory</InputLabel>
                <Select
                  label="Subcategory"
                  value={dlg.subcategoryId}
                  onChange={(e) => {
                    const row = allRows.find((r) => r.id === e.target.value);
                    setDlg((d) => ({ ...d, subcategoryId: e.target.value as string, label: row?.terms_label || '' }));
                  }}
                >
                  {(cats.find((c) => c.id === dlg.categoryId)?.subcategories || []).map((sub) => (
                    <MenuItem key={sub.id} value={sub.id}>
                      {sub.name}
                      {sub.terms_pdf_path && (
                        <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>(has PDF)</Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : (
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Subcategory</Typography>
              <Typography variant="body2" fontWeight={600}>
                {selectedSubcategory?.category_name} → {selectedSubcategory?.name}
              </Typography>
            </Box>
          )}

          {/* Label */}
          <TextField
            label="Customer label"
            size="small"
            fullWidth
            placeholder="e.g. Vocus Mobile T&C Jan 2026"
            helperText="Shown as the document heading on the customer's order review page."
            value={dlg.label}
            onChange={(e) => setDlg((d) => ({ ...d, label: e.target.value }))}
          />

          {/* PDF picker */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              PDF file {dlg.mode === 'edit' && '(leave blank to keep existing)'}
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} size="small">
                Choose PDF
                <input
                  type="file"
                  hidden
                  accept="application/pdf,.pdf"
                  onChange={(e) => {
                    setDlg((d) => ({ ...d, file: e.target.files?.[0] ?? null }));
                    e.target.value = '';
                  }}
                />
              </Button>
              {dlg.file ? (
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 260 }}>
                  {dlg.file.name}
                </Typography>
              ) : dlg.mode === 'edit' && selectedSubcategory?.terms_pdf_path ? (
                <Typography variant="body2" color="success.main">Current PDF will be kept</Typography>
              ) : (
                <Typography variant="body2" color="text.disabled">No file chosen</Typography>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDlg} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || (!dlg.subcategoryId) || (dlg.mode === 'add' && !dlg.file)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderTermsSettingsTab;
